// AR/EN tab pair for editing one bilingual JSONB field (motiflink §1.6).
//
// Pairs with extractField/patchField in ./bilingual.ts: this owns the tab
// chrome and the per-locale `dir`, the caller owns the input. A render prop
// rather than a fixed <Textarea> because the consumers differ — M3 edits email
// bodies, M4 edits CMS block prose, M6 edits blog posts.
//
// The empty-dot indicator is the point of the component: with cross-language
// fallback dead (motiflink §1.2), an untranslated Arabic field renders as a
// blank on the live site. Making "this tab has nothing in it" visible without
// clicking the tab is what stops that shipping unnoticed.

import { LOCALES, localeDirection, type Locale } from '@fajrarabi/i18n';
import { useId, useRef, useState, type ReactNode } from 'react';

import { extractField, type BilingualValue } from './bilingual';
import { cn } from './cn';

/** Native names, so the Arabic tab is labelled in Arabic. */
const LOCALE_LABELS: Record<Locale, string> = {
  ar: 'العربية',
  en: 'English',
};

export interface BilingualTabsProps {
  /** The field being edited, in its stored JSONB shape. */
  value: BilingualValue;
  /**
   * Renders the input for one locale. `dir` is the direction for THAT locale,
   * not the page — an Arabic value must render RTL inside the LTR admin.
   */
  children: (args: {
    locale: Locale;
    value: string;
    dir: 'ltr' | 'rtl';
    id: string;
    onChange: (next: string) => void;
  }) => ReactNode;
  onChange: (locale: Locale, next: string) => void;
  /** Tab shown first. Defaults to Arabic, the product's primary language. */
  defaultLocale?: Locale;
  /** Accessible name for the tablist. */
  ariaLabel?: string;
  className?: string;
}

export function BilingualTabs({
  value,
  children,
  onChange,
  defaultLocale = 'ar',
  ariaLabel = 'Language',
  className,
}: BilingualTabsProps) {
  const [active, setActive] = useState<Locale>(defaultLocale);
  const baseId = useId();
  const tabRefs = useRef<Partial<Record<Locale, HTMLButtonElement | null>>>({});

  // Arrow-key roving focus — required for role="tablist" to be usable by
  // keyboard, which the WCAG 2.1 AA floor in CLAUDE.md §5 makes non-optional.
  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const back = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
    if (!forward && !back) return;
    event.preventDefault();

    const index = LOCALES.indexOf(active);
    const nextIndex = (index + (forward ? 1 : -1) + LOCALES.length) % LOCALES.length;
    const next = LOCALES[nextIndex]!;
    setActive(next);
    tabRefs.current[next]?.focus();
  }

  const activeValue = extractField(value, active);

  return (
    <div className={className}>
      <div role="tablist" aria-label={ariaLabel} className="flex gap-1">
        {LOCALES.map((locale) => {
          const selected = locale === active;
          const empty = extractField(value, locale).trim() === '';
          return (
            <button
              key={locale}
              ref={(el) => {
                tabRefs.current[locale] = el;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${locale}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${locale}`}
              // Only the active tab is in the tab order; arrows move between them.
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(locale)}
              onKeyDown={handleKeyDown}
              className={cn(
                'flex items-center gap-1.5 rounded-t-xl px-3 py-1.5 text-sm font-semibold transition-colors',
                selected ? 'bg-burgundy-500 text-cream-50' : 'text-burgundy-500 hover:bg-cream-200',
              )}
            >
              <span dir={localeDirection(locale)}>{LOCALE_LABELS[locale]}</span>
              {empty && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    selected ? 'bg-cream-50/70' : 'bg-heart',
                  )}
                />
              )}
              {empty && <span className="sr-only">(empty)</span>}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`${baseId}-panel-${active}`}
        aria-labelledby={`${baseId}-tab-${active}`}
        // Logical corners (CLAUDE.md §5: no physical directional CSS) — the
        // start-top corner stays square so the panel reads as attached to the
        // first tab in LTR and RTL alike.
        className="rounded-2xl rounded-ss-none bg-cream-50/50 p-3 ring-1 ring-burgundy-500/10"
      >
        {children({
          locale: active,
          value: activeValue,
          dir: localeDirection(active),
          id: `${baseId}-input-${active}`,
          onChange: (next) => onChange(active, next),
        })}
      </div>
    </div>
  );
}
